import { AB_PIN_DETAIL_FULLSCREEN } from "@/constants/ABMng";
import { MATERIAL_ICON_ITEM_MAP } from "@/constants/materialIcon";
import { PIN_MATERIAL_STATUS } from "@/constants/pinMaterialStatus";
import { PIN_OPEN_TYPE } from "@/constants/pinOpenType";
import useAppStore from "@/context/store";
import { cancelShieldPin } from "@/services";
import { MaterialCard } from "@/tracker-v2";
import { OperationType } from "@/types/inspirationWords";
import { canEditPinFn } from "@/utils/board";
import displayNum from "@/utils/displayNum";
import { getContentType } from "@/utils/getContentType";
import getNextDetailsSetp from "@/utils/getHistorySetp";
import useABTest from "@/utils/hooks/useABtest";
import { usePinCardMagnifier } from "@/utils/hooks/usePinCardMagnifier";
import usePngBg from "@/utils/hooks/usePngBg";
import { isMouseLeave } from "@/utils/isMouseLeave";
import { useMemoizedFn, useUpdateEffect } from "ahooks";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import qs from "qs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { When } from "react-if";
import { EVENT } from "../../constants/event";
import imageUrl from "../../utils/imageUrl";
import AiTag from "../AiTag";
import HBAvatar from "../Avatar";
import HBIcon from "../HBIcon";
import Image from "../Image";
import { useLogin } from "../LoginModal";
import { usePinActionsWithCard } from "../PinCard/actions/usePinActionWithCard";
import { TrackerA, TrackerDiv } from "../Tracker";
import { usePinReport } from "./actions/usePinReport";
import { type IPurePinCardProps } from "./common";
import styles from "./index.module.less";
import MagnifierPreview from "./MagnifierPreview";
import { useMagnifierState } from "./MagnifierPreview/useMagnifierState";
import PurePinMore from "./more";
import { Shield } from "./shield";
import PinVideo from "./video";

const PurePinActions = dynamic(import("../PurePinCard/actions"), {
  ssr: false,
});

function PurePinCard({
  user,
  pinOpenType,
  record,
  hideUser,
  hideCount = true,
  disableModal,
  disableFile,
  fullWidth,
  showMaterialTag,
  isModal,
  showDesc,
  imgLoading = "lazy",
  searchWord,
  width,
  showDownload,
  hasPinCreated,
  showLikeBtn,
  moduleName,
  currentBoard, // 新增：当前画板信息，用于画板选择器默认值
  enableCurrentBoardPriority, // 新增：是否启用当前画板优先逻辑
  recordOperation, // 灵感词操作记录回调
  trackerData,
  isSearchPage, // 新增：是否在搜索结果页（用于渐进式搜索功能）
}: IPurePinCardProps) {
  // 获取全屏详情页 AB 测试结果
  const pinDetailFullscreen = useABTest(AB_PIN_DETAIL_FULLSCREEN);
  const isFullScreenMode = pinDetailFullscreen.newFull;

  // 获取放大镜功能 AB 测试结果
  const { showMagnifier } = usePinCardMagnifier();

  const [hover, setHover] = useState(false);
  const [play, setPlay] = useState(false);
  const [pin, setPin] = useState(record);
  const [bgColor, setBgColor] = useState(record?.file?.theme || "EDF2F5");
  const { login } = useLogin();
  const router = useRouter();
  const currentSrcRef = useRef(null);
  const imageRef = useRef(null);
  const isUserPin = useMemo(() => {
    return (pin?.user?.user_id || pin?.user_id) === user?.user_id;
  }, [pin, user]);

  const { pinActionsProps, onMouseEnterCard, onMouseLeaveCard, contextValue } =
    usePinActionsWithCard(true);

  const isGif = useMemo(() => {
    return pin.file?.type === "image/gif";
  }, [pin]);
  const meterialCategory = useMemo(() => {
    return pin.file_material?.material_category;
  }, [pin]);
  const isVideo = useMemo(() => {
    return meterialCategory === "视频模板" || meterialCategory === "视频";
  }, [meterialCategory]);

  const contentType = useMemo(() => {
    return getContentType(pin);
  }, [pin]);
  const videoTime = useMemo(() => {
    let duration = pin.file_material?.extra?.duration;
    duration = ~~(duration / 1000);
    const hh = Math.floor(duration / 3600);
    duration -= hh * 3600;
    const mm = Math.floor(duration / 60);
    duration -= mm * 60;
    const ss = duration;
    const appendZeroStr = (num: number) => (num < 10 ? "0" + num : num);
    return `${appendZeroStr(mm)}:${appendZeroStr(ss)}`;
  }, [pin]);

  useEffect(() => {
    const onUpdate = (evt: CustomEvent<IPin>) => {
      const record = evt.detail;
      if (record?.pin_id === pin?.pin_id) {
        // 保留feed流中特殊属性，避免feed流中 sourceType 相关字段丢失
        setPin({
          ...pin,
          ...record,
        });
      }
    };

    window.addEventListener(EVENT.UPDATE_PIN, onUpdate);

    return () => {
      window.removeEventListener(EVENT.UPDATE_PIN, onUpdate);
    };
  }, [pin]);

  useUpdateEffect(() => {
    setPin(record);
  }, [record]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const hoverDelay = useRef<NodeJS.Timeout | null>(null);
  const onHover = useCallback(
    (hover) => {
      const video = videoRef.current;
      // 停止播放，减少视频加载流量， 不做处理视频的请求会一直继续加载
      if (video && !hover) {
        video.pause();
        video.src = "";
        // video.load();
      }
      if (hover && (isVideo || isGif)) {
        // 延迟视频加载
        hoverDelay.current = setTimeout(() => {
          setHover(hover);

          if (isGif) {
            setPlay(true); // gif图移动上去后永远播放
          }
        }, 300);
      } else {
        if (hoverDelay.current) {
          clearTimeout(hoverDelay.current);
        }
        setHover(hover);
      }
    },
    [isGif, isVideo]
  );

  const onDetail = useCallback(
    (evt) => {
      if (evt.metaKey || evt.ctrlKey || pinOpenType === PIN_OPEN_TYPE.BLANK) {
        return;
      }

      // 调用灵感词操作记录回调（使用 file_id）
      if (recordOperation && pin?.file_id) {
        recordOperation(String(pin.file_id), OperationType.CLICK);
      }

      onHover(false);
      onMouseLeaveCard && onMouseLeaveCard();

      evt.preventDefault();

      if (disableModal) {
        // 用于渐进式显示图片，先显示缓存图，再显示大图，但是这个router.push的方式所以只能把缓存图片地址通过window传递
        // currentSrcRef.current 可能为 null，需要防御
        window._pinDetailCurrentSrc = currentSrcRef.current || "";
        router.push(`/pins/${pin.pin_id}`);
        return;
      }

      if (isFullScreenMode) {
        // 全屏详情页：使用多弹窗堆栈模式
        const { pushPinToStack } = useAppStore.getState();
        const pinData = {
          ...pin,
          pinId: pin.pin_id,
          // 用于渐进式显示图片，先显示缓存图，再显示大图
          currentSrc: currentSrcRef.current,
          ...(searchWord ? { searchWord } : null),
          ...(!disableFile ? { file: pin.file } : null),
        };

        pushPinToStack(pinData);
        let historyData =
          typeof history?.state === "object" ? history?.state : {};
        const search = qs.stringify({ searchWord }, { addQueryPrefix: true });
        // 使用 pinId 而不是 stackIndex 来标识弹窗
        // 添加序列号用于判断前进/后退方向
        const nextSeq = (historyData.seq || 0) + 1;
        window.history.pushState(
          {
            ...historyData,
            modalPinId: pin.pin_id,
            isModal: true,
            seq: nextSeq,
          },
          "",
          `/pins/${pin.pin_id}${search}`
        );
      } else {
        // 非全屏详情页：使用原有的单弹窗模式
        useAppStore.setState({
          currentPin: {
            ...pin,
            pinId: pin.pin_id,
            // 用于渐进式显示图片，先显示缓存图，再显示大图
            currentSrc: currentSrcRef.current,
            ...(searchWord ? { searchWord } : null),
            ...(!disableFile ? { file: pin.file } : null),
          },
        });

        // 弹窗内切换需要记录push的步骤
        let detailsSetp = getNextDetailsSetp();
        let historyData =
          typeof history?.state === "object" ? history?.state : {};
        const search = qs.stringify({ searchWord }, { addQueryPrefix: true });
        window.history.pushState(
          isModal ? { ...historyData, detailsSetp } : null,
          "",
          `/pins/${pin.pin_id}${search}`
        );
      }
    },
    [
      pinOpenType,
      onHover,
      onMouseLeaveCard,
      pin,
      disableModal,
      disableFile,
      isModal,
      router,
      searchWord,
      isFullScreenMode,
    ]
  );

  const imageBgClass = usePngBg(record?.file?.theme || "");

  const onImgLoad = useCallback((evt) => {
    currentSrcRef.current = evt.target.currentSrc;
    setBgColor("");

    // 只有png图片才需要判断背景
    // if (pin.file?.type === "image/png") {
    //   onImgLoadCallback(evt);
    // }
  }, []);

  const userDom = useMemo(() => {
    return (
      <TrackerA
        className={styles.user}
        href={`/user/${pin.user?.urlname}`}
        target="_blank"
        data-gd-click="button_click"
        data-button-name="去个人页"
        data-content-id={pin.pin_id}
        data-board-id={pin.board_id}
        data-search-word={searchWord}
        {...(moduleName ? { "data-module-name": moduleName } : null)}
        title={pin.user?.username}
      >
        <div className={styles.avatar}>
          <HBAvatar user={pin.user} size={28} shape="circle" />
        </div>
        <div className={styles.name}>{pin.user?.username}</div>
      </TrackerA>
    );
  }, [pin, moduleName, searchWord]);

  const { onReport } = usePinReport(pin, contentType, searchWord);

  /** 屏蔽 */
  const onShield = useCallback(
    async (pinInfo) => {
      if (!user) {
        const result = await login({
          source_location: "采集屏蔽",
        });
        if (!result) {
          return;
        }
      }

      setPin((pin) => ({
        ...pin,
        ...pinInfo,
        shieldStatus: "show",
      }));
      // 必须更新一下，不然瀑布流销毁状态就会消失
      record.shieldStatus = "show";
    },
    [login, user, record]
  );

  /** 取消屏蔽*/
  const onCancelShield = useMemoizedFn(async () => {
    const result = await cancelShieldPin({
      item_type: 1,
      item_value: pin?.file_id,
    });

    if (!result.err) {
      setPin((pin) => ({
        ...pin,
        shieldStatus: "",
      }));
      // 必须更新一下，不然瀑布流销毁状态就会消失
      record.shieldStatus = "";
    }
  });

  const wrapRef = useRef<HTMLDivElement>(null);

  const onMouseLeave = useCallback(
    (e) => {
      if (isMouseLeave(e, wrapRef.current as HTMLElement)) {
        onHover(false);
        onMouseLeaveCard && onMouseLeaveCard();
      }
    },
    [onHover, onMouseLeaveCard, pin]
  );

  const onMouseEnter = useMemoizedFn(() => {
    onHover(true); // 解决关闭弹窗后，没有触发鼠标MouseEnter事件
    onMouseEnterCard?.({
      pin,
      imageDom: imageRef.current,
      onUpdatePin: setPin,
    });

    if (pin.mask) {
      pin.mask = false;
      window.dispatchEvent(new Event(EVENT.HIDE_MASK));
    }
  });

  const onOperate = () => {
    // 关注页面需要记录用户有没有操作
    if (router.pathname === "/follow") {
      window.dispatchEvent(new Event(EVENT.CARD_OPERATE));
    }
  };

  // 右键菜单处理器：记录灵感词操作，但不阻止浏览器默认行为
  const handleContextMenu = useCallback(
    (e) => {
      // 执行灵感词记录逻辑
      if (pin?.pin_id && recordOperation) {
        recordOperation(String(pin.file_id), OperationType.RIGHT_CLICK);
      }
    },
    [pin, recordOperation]
  );

  const materialIcon = useMemo(() => {
    return (
      MATERIAL_ICON_ITEM_MAP[pin.file_material?.material_type?.toLowerCase()] ||
      MATERIAL_ICON_ITEM_MAP["default"]
    );
  }, [pin]);

  const canEditPin = useMemo(() => {
    return canEditPinFn(pin, user);
  }, [pin, user]);

  // 使用放大镜状态机 Hook
  const {
    phase: magnifierPhase,
    shouldRender: shouldRenderMagnifier,
    handleAnimationEnd: handleMagnifierAnimationEnd,
    handleImageMouseEnter,
    handleImageMouseLeave,
    handlePreviewMouseEnter,
    handlePreviewMouseLeave,
  } = useMagnifierState({
    pinId: String(pin.pin_id),
    isVideo,
    showMagnifier,
  });

  return (
    <div
      ref={wrapRef}
      className={classNames(styles.card, {
        [styles.fullWidth]: fullWidth,
      })}
      data-pin-id={pin.pin_id}
      {...(moduleName ? { "data-module-name": moduleName } : null)}
      onClick={onOperate}
      onContextMenu={handleContextMenu}
      // onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseEnter}
    >
      <div className={styles.container}>
        {/* 图片 */}
        <TrackerDiv
          ref={imageRef}
          className={styles.img}
          data-gd-expose="content_expose"
          data-gd-click="material_click"
          data-unique-id={pin.pin_id}
          data-content-id={pin.pin_id}
          data-content-type={contentType}
          data-material-id={pin.file_material?.material_id}
          data-ext={JSON.stringify(pin?.ext)}
          data-board-id={pin.board_id}
          data-file-id={pin.file_id}
          data-material-type={
            pin?.file_material?.extra?.group_ids ? "套系素材" : ""
          }
          {...(moduleName ? { "data-module-name": moduleName } : null)}
          {...(searchWord ? { "data-search-word": searchWord } : null)}
          data-check-cover
          style={bgColor ? { backgroundColor: `#${bgColor}` } : undefined}
          {...trackerData}
        >
          <MaterialCard
            pin={pin}
            moduleName={moduleName}
            searchWord={searchWord}
            materialType={
              pin?.file_material?.extra?.group_ids ? "套系素材" : undefined
            }
          >
            <AiTag pin={pin} hide={hover} size="md" />
            <div className={styles.bottomInfo}>
              {showMaterialTag &&
                pin.file_material?.status === PIN_MATERIAL_STATUS.AVAILABLE && (
                  <div className={classNames(styles.material, styles.item)}>
                    <div className={styles.icon}>
                      <HBIcon
                        className={styles.copyright}
                        type="ic_copyright"
                      />
                    </div>
                    <div className={styles.text}>
                      {materialIcon?.label || "素材"}
                    </div>
                  </div>
                )}
              {isGif && !isVideo && !play && (
                <div className={classNames(styles.gif, styles.item)}>GIF</div>
              )}
              {isVideo && !hover && (
                <div className={classNames(styles.video, styles.item)}>
                  {videoTime}
                </div>
              )}
            </div>
            {hover && !isUserPin && !showLikeBtn && !canEditPin && (
              <PurePinMore
                pin={pin}
                onReport={onReport}
                onShield={onShield}
                searchWord={searchWord}
                trackerData={trackerData}
                isSearchPage={isSearchPage}
              />
            )}
            <a
              className={styles.link}
              href={`/pins/${pin.pin_id}${qs.stringify(
                { searchWord },
                { addQueryPrefix: true }
              )}`}
              target="_blank"
              onClick={onDetail}
            >
              {isVideo && hover && <PinVideo fullWidth={fullWidth} pin={pin} />}

              <Image
                className={imageBgClass}
                alt={pin.raw_text}
                src={imageUrl(pin.file, {
                  width: fullWidth ? 1200 : 240,
                  gif: play && isGif,
                })}
                srcSet={
                  imageUrl(pin.file, {
                    width: fullWidth ? 1200 * 2 : 480,
                    gif: play && isGif,
                  }) + " 2x"
                }
                width={Math.min(fullWidth ? 1200 : width, pin.file?.width)}
                height={Math.round(
                  (pin.file?.height / pin.file?.width) *
                    Math.min(fullWidth ? 1200 : width, pin.file?.width)
                )}
                loading={imgLoading}
                style={bgColor ? { background: "none" } : null}
                onLoad={onImgLoad}
                showError={true}
                title={pin.raw_text}
                crossOrigin="anonymous"
                onMouseEnter={handleImageMouseEnter}
                onMouseLeave={handleImageMouseLeave}
              />
              <span style={{ display: "none" }}>{pin?.raw_text}</span>
              {record.mask && <div className={styles.mask}>刚刚访问</div>}
            </a>
          </MaterialCard>
        </TrackerDiv>
        <When condition={showDesc && pin?.raw_text}>
          <a
            className={styles.pinDesc}
            title={pin?.raw_text || ""}
            href={`/pins/${pin.pin_id}${qs.stringify(
              { searchWord },
              { addQueryPrefix: true }
            )}`}
            target="_blank"
            onClick={onDetail}
          >
            {pin?.raw_text || ""}
          </a>
        </When>
        <When condition={!hideCount}>
          <div className={styles.num}>
            <div title={`转采：${pin?.repin_count}`}>
              <HBIcon className={styles.icon} type="ic_zc1" />
              <span className={styles.text}>
                {displayNum(pin?.repin_count)}
              </span>
            </div>
            <div title={`喜欢：${pin?.like_count}`}>
              <HBIcon
                className={styles.icon}
                type={pin?.liked ? "ic_favorite" : "ic_like"}
              />
              <span className={styles.text}>{displayNum(pin?.like_count)}</span>
            </div>
          </div>
        </When>
        {/* 用户信息 */}
        <When condition={!hideUser}>
          <div className={styles.info}>{userDom}</div>
        </When>
      </div>
      {pin?.shieldStatus && (
        <Shield
          record={pin}
          contentType={contentType}
          onCancelShield={onCancelShield}
        />
      )}
      {pinActionsProps.position && (
        <PurePinActions
          {...pinActionsProps}
          offsetParent={wrapRef.current as HTMLElement}
          showDownload={showDownload}
          hasPinCreated={hasPinCreated}
          needHoldPinAction={true}
          contextValue={contextValue}
          showLikeBtn={showLikeBtn}
          searchWord={searchWord}
          moduleName={moduleName}
          currentBoard={currentBoard}
          enableCurrentBoardPriority={enableCurrentBoardPriority}
          showMeOperation
          trackerData={trackerData}
        />
      )}
      {/* 放大镜浮窗 - 受AB实验控制，仅通过 Alt 键触发 */}
      {shouldRenderMagnifier && (
        <MagnifierPreview
          pin={pin}
          cardRef={imageRef}
          phase={magnifierPhase}
          onAnimationEnd={handleMagnifierAnimationEnd}
          onMouseEnter={handlePreviewMouseEnter}
          onMouseLeave={handlePreviewMouseLeave}
        />
      )}
    </div>
  );
}

export default React.memo(PurePinCard);
